import {
  computed,
  effect,
  ElementRef,
  inject,
  Injectable,
  Signal,
  signal,
  untracked,
  WritableSignal,
} from '@angular/core';
import {
  Coordinate,
  DistancePipe,
  GpsOnly,
  GpsOnlyAuthor,
  Unit,
} from '@kinomap-dev/ng-ui-lib/core';
import { SettingsService } from './settings.service';
import { ProfileService } from './profile.service';
import { FormGroup } from '@angular/forms';
import { ContentType } from '../pages/content-creation/content-creation';
import { Toaster } from './toaster';
import { TranslateService } from '@ngx-translate/core';
import { PlayerService } from '@kinomap-dev/ng-ui-lib/player';
import { GpsUploadControlStatus } from '../components/gps-uploader/gps-upload-control-item';
import { SynchroState } from '../types/synchroStatus.type';

export type Selection = { start: number; end: number };
export type SelectionCoordinate = { start: Coordinate; end: Coordinate };
export type GpsUploadControl = { text: string; status: GpsUploadControlStatus; validate: boolean };

export const SYNC_DURATION_TOLERANCE_SECONDS = 3;

@Injectable({
  providedIn: 'root',
})
export class MediaBlockService {
  public readonly settingService: SettingsService = inject(SettingsService);
  private readonly profileService: ProfileService = inject(ProfileService);
  private readonly playerService: PlayerService = inject(PlayerService);
  private readonly toaster: Toaster = inject(Toaster);
  private readonly translateService: TranslateService = inject(TranslateService);

  mediaForm: FormGroup | undefined;
  contentType: ContentType | undefined;

  videoFile: WritableSignal<File | undefined> = signal(undefined);
  gpsFile: WritableSignal<File | undefined> = signal(undefined);
  videoUrl: WritableSignal<string | undefined> = signal(undefined);

  chartLabels: WritableSignal<number[] | undefined> = signal(undefined);
  chartData: WritableSignal<number[] | undefined> = signal(undefined);
  chartDistances: WritableSignal<number[] | undefined> = signal(undefined);

  syncedCoordinates: WritableSignal<Coordinate[]> = signal<Coordinate[]>([]);
  rawCorrdinates: Signal<Coordinate[]> = this.syncedCoordinates;

  gpsTrackValidated: WritableSignal<boolean> = signal(false);
  distancePipe: DistancePipe = new DistancePipe();
  unit: Unit = this.settingService.unit();
  informations: WritableSignal<GpsOnly> = signal<GpsOnly>({} as GpsOnly);

  videoFileData: WritableSignal<{ name: string; duration: number; weight: number } | undefined> =
    signal(undefined);
  gpsFileData: WritableSignal<
    | {
        name: string;
        duration: number;
        weight: number;
        cadence: boolean;
        power: boolean;
        heartRate: boolean;
      }
    | undefined
  > = signal(undefined);

  inputVideoRefs: WritableSignal<ElementRef<HTMLInputElement>[]> = signal([]);

  // Contrôles de validité du tracé GPS : partagés entre toutes les instances de
  // app-gps-uploader (bloc média + fenêtre fullscreen) pour que le résultat des
  // contrôles reste cohérent quel que soit l'endroit où le fichier a été importé.
  gpsFilesSupport: string[] = ['gpx', 'fit', 'nmea', 'tcx'];
  isLoadingTrack: WritableSignal<boolean> = signal(false);

  controlGpsCompatibility: WritableSignal<GpsUploadControl> = signal({
    text: 'gps_uploader.controls.compatible',
    status: 'pending',
    validate: false,
  });
  controlGpsDuration: WritableSignal<GpsUploadControl> = signal({
    text: 'gps_uploader.controls.similar_duration',
    status: 'pending',
    validate: false,
  });
  controlGpsSpeed: WritableSignal<GpsUploadControl> = signal({
    text: 'gps_uploader.controls.speed_coordinates',
    status: 'pending',
    validate: false,
  });
  controlGpsData: WritableSignal<GpsUploadControl> = signal({
    text: 'gps_uploader.controls.data_analysis',
    status: 'pending',
    validate: false,
  });

  controlsDone: Signal<boolean> = computed(() => {
    const done: GpsUploadControlStatus[] = ['complete', 'error'];
    return [
      this.controlGpsCompatibility(),
      this.controlGpsDuration(),
      this.controlGpsSpeed(),
      this.controlGpsData(),
    ].every((c) => done.includes(c.status));
  });

  controlsStarted: Signal<boolean> = computed(() =>
    [
      this.controlGpsCompatibility(),
      this.controlGpsDuration(),
      this.controlGpsSpeed(),
      this.controlGpsData(),
    ].some((c) => c.status !== 'pending'),
  );

  controlsHaveError: Signal<boolean> = computed(() =>
    [
      this.controlGpsCompatibility(),
      this.controlGpsDuration(),
      this.controlGpsSpeed(),
      this.controlGpsData(),
    ].some((c) => c.status === 'error'),
  );

  gpsLoading: Signal<boolean> = computed(
    () =>
      this.isLoadingTrack() ||
      (this.controlsStarted() && !this.controlsDone() && !this.controlsHaveError()),
  );

  // Synchronisation vidéo / GPS
  syncMarkerCoordinate: WritableSignal<Coordinate | undefined> = signal(undefined);
  /** Modale fullscreen : l'utilisateur est en train de synchroniser (tutos affichés). */
  syncMode: WritableSignal<boolean> = signal(false);
  progressInVideo: WritableSignal<number | undefined> = signal(undefined);
  /** Point survolé sur le profil du tracé, suivi par l'avatar sur la carte. */
  hoveredCoordinate: WritableSignal<Coordinate | undefined> = signal(undefined);
  syncroTime: Signal<number | undefined> = computed(() => this.playerService.progress());
  videoStartTime: WritableSignal<number | undefined> = signal(undefined);

  /** Décalage en secondes entre le début du tracé GPS et le début de la vidéo. */
  syncOffsetSeconds: Signal<number> = computed(() => {
    const progressInVideo = this.progressInVideo();
    const coords = this.syncedCoordinates();
    if (progressInVideo === undefined || coords.length === 0) return 0;
    return Math.max(0, progressInVideo - coords[0].coo_time);
  });

  selectionWidth: Signal<number> = computed(() => {
    const end: number = this.selection()?.end ?? 0;
    return (end * 100) / (this.cumuledTime() ?? 0);
  });

  selection: Signal<Selection | undefined> = computed<Selection | undefined>(() => {
    const videoFileData: { name: string; duration: number; weight: number } | undefined =
      this.videoFileData();
    const gpsFileData:
      | {
          name: string;
          duration: number;
          weight: number;
          cadence: boolean;
          power: boolean;
          heartRate: boolean;
        }
      | undefined = this.gpsFileData();

    if (!videoFileData || !gpsFileData) return undefined;

    const videoDuration: number = videoFileData.duration;
    const offset: number = this.syncOffsetSeconds();

    return { start: offset, end: offset + videoDuration };
  });

  cumuledTime: Signal<number | undefined> = computed(() => {
    const videoDuration: number | undefined = this.videoFileData()?.duration;
    const gpsDuration: number | undefined = this.gpsFileData()?.duration;
    if (!videoDuration && !gpsDuration) return undefined;
    if (!videoDuration || !gpsDuration) return (videoDuration ?? 0) + (gpsDuration ?? 0);
    if (videoDuration > gpsDuration) return this.videoFileData()!.duration;
    return this.gpsFileData()!.duration;
  });

  /** Durée totale effective : tient compte du décalage de synchronisation.
   * Si la vidéo déborde au-delà du tracé GPS, cette valeur est plus grande que cumuledTime. */
  effectiveTotalDuration: Signal<number | undefined> = computed(() => {
    const cumuled = this.cumuledTime();
    const sel = this.selection();
    if (!cumuled) return undefined;
    return Math.max(cumuled, sel?.end ?? 0);
  });

  hasGps: Signal<boolean> = computed<boolean>(() => !!(this.gpsFile() && this.gpsFileData()));
  hasVideo: Signal<boolean> = computed<boolean>(() => !!(this.videoFile() && this.videoFileData()));
  hasVideoAndGps: Signal<boolean> = computed<boolean>(() => !!(this.hasGps() && this.hasVideo()));

  selectionCoordinate: Signal<SelectionCoordinate | undefined> = computed(() => {
    const coords: Coordinate[] = this.syncedCoordinates();
    if (!coords.length) return undefined;
    const sel: Selection | undefined = this.selection();
    if (!sel) return { start: coords[0], end: coords[coords.length - 1] };
    const firstTime: number = coords[0].coo_time;
    const closest = (target: number): Coordinate =>
      coords.reduce((prev, curr) =>
        Math.abs(curr.coo_time - target) < Math.abs(prev.coo_time - target) ? curr : prev,
      );
    return { start: closest(firstTime + sel.start), end: closest(firstTime + sel.end) };
  });

  // ⚠️ contrôles & erreurs !
  /** Écart en secondes entre la durée vidéo et la durée GPS (positif si la vidéo est plus longue). */
  durationGap: Signal<number | undefined> = computed(() => {
    const videoDuration: number | undefined = this.videoFileData()?.duration;
    const gpsDuration: number | undefined = this.gpsFileData()?.duration;
    if (!gpsDuration || !videoDuration) return undefined;
    return videoDuration - gpsDuration;
  });

  videoLongerThanGps: Signal<boolean> = computed(() => {
    const gap: number | undefined = this.durationGap();
    return gap !== undefined && gap > SYNC_DURATION_TOLERANCE_SECONDS;
  });

  /** Vidéo et GPS ont quasiment la même durée : la synchro est probablement déjà bonne en les alignant au début. */
  similarDurations: Signal<boolean> = computed(() => {
    const gap: number | undefined = this.durationGap();
    return gap !== undefined && Math.abs(gap) <= SYNC_DURATION_TOLERANCE_SECONDS;
  });

  speedWarn: Signal<boolean> = computed(() => false);
  syncroComplete: Signal<boolean> = computed(() => this.progressInVideo() !== undefined);
  noSyncError: Signal<boolean> = computed(() => this.progressInVideo() === undefined);
  haveError: Signal<boolean> = computed(() => this.noSyncError() || this.videoLongerThanGps());
  syncModeActive: Signal<boolean> = computed(
    () => this.syncMode() && this.hasVideoAndGps() && !this.videoLongerThanGps(),
  );

  /** État unique de la synchro, par ordre de priorité. Source de vérité pour `app-syncro-status`. */
  synchroState: Signal<SynchroState> = computed<SynchroState>(() => {
    if (!this.hasVideoAndGps()) return 'no-media';
    if (this.videoLongerThanGps()) return 'gps-too-short';
    if (this.speedWarn()) return 'speed-warning';
    if (this.syncroComplete()) return 'synced';
    if (this.similarDurations()) return 'likely-synced';
    return 'not-synced';
  });

  gpsHaveAdditionalData: Signal<boolean> = computed(
    () =>
      !!(this.gpsFileData()?.cadence || this.gpsFileData()?.power || this.gpsFileData()?.heartRate),
  );

  syncroStatus: Signal<'complete' | 'in_progress' | 'no_sync' | null> = computed(() => {
    if (!this.hasVideoAndGps()) return null;
    if (this.syncroComplete()) return 'complete';
    if (this.hasVideoAndGps() && this.syncroTime()) return 'in_progress';
    return 'no_sync';
  });

  constructor() {
    effect(() => {
      if (this.videoLongerThanGps()) {
        this.toaster.error(
          undefined,
          this.translateService.instant('gps_uploader.errors.shorter_than_video'),
          'warning',
          5000,
        );
      }
    });

    // Hors mode synchro, aucun repère ne doit rester sur la carte (modale fermée, tracé devenu trop court…).
    effect(() => {
      if (!this.syncModeActive()) untracked(() => this.syncMarkerCoordinate.set(undefined));
    });

    effect(() => {
      const time = this.progressInVideo();
      if (time !== undefined) {
        this.mediaForm?.patchValue({ syncroTime: time });
        this.mediaForm?.get('syncroTime')?.markAsTouched();
      }
    });
  }

  updateMapInformations(): void {
    const profile: any = this.profileService.profile();
    const author: GpsOnlyAuthor = {
      id: profile?.id ?? 0,
      username: profile?.username || profile?.email || 'Creator',
      full_name: profile?.full_name || profile?.username || 'Creator',
      gender: profile?.gender || '',
      avatar_url: profile?.avatar_url,
      is_followed: false,
      is_contributor: false,
      country: profile?.country ?? ({} as any),
      activity_sessions_count: 0,
      followers_count: 0,
      birthdate: profile?.birthdate || '',
      description: null,
      age: profile?.age ?? 0,
    };

    this.informations.set({ ...this.informations(), author });
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  resetControls(): void {
    this.controlGpsCompatibility.set({
      text: 'gps_uploader.controls.compatible',
      status: 'pending',
      validate: false,
    });
    this.controlGpsDuration.set({
      text: 'gps_uploader.controls.similar_duration',
      status: 'pending',
      validate: false,
    });
    this.controlGpsSpeed.set({
      text: 'gps_uploader.controls.speed_coordinates',
      status: 'pending',
      validate: false,
    });
    this.controlGpsData.set({
      text: 'gps_uploader.controls.data_analysis',
      status: 'pending',
      validate: false,
    });
  }

  async runGpsControls(ext: string, coordinates: Coordinate[]): Promise<void> {
    this.gpsTrackValidated.set(false);
    this.resetControls();
    await this.controlFormat(ext, coordinates);
    if (!coordinates.length) return;
    await this.controlDuration(coordinates);
    await this.controlSpeedCoordinates(coordinates);
    await this.controlData(coordinates);
    this.gpsTrackValidated.set(!this.controlsHaveError());
  }

  private async controlFormat(ext: string, coordinates: Coordinate[]): Promise<void> {
    this.controlGpsCompatibility.update((c) => ({ ...c, status: 'in_progress' }));
    await this.delay(600);
    const validExt: boolean = this.gpsFilesSupport.includes(ext);
    const hasCoordinates: boolean = coordinates.length > 0;
    const isCompatible: boolean = validExt && hasCoordinates;
    let text: string;
    if (!validExt) text = 'gps_uploader.controls.unsupported_format';
    else if (!hasCoordinates) text = 'gps_uploader.controls.no_coordinates';
    else text = 'gps_uploader.controls.compatible';
    this.controlGpsCompatibility.set({
      text,
      status: isCompatible ? 'complete' : 'error',
      validate: false,
    });
  }

  private async controlDuration(coordinates: Coordinate[]): Promise<void> {
    this.controlGpsDuration.update((c) => ({ ...c, status: 'in_progress' }));
    await this.delay(600);
    const duration = coordinates.at(-1)!.coo_time - coordinates[0]!.coo_time;
    const isValid = duration > 0 && coordinates.length >= 10;
    this.controlGpsDuration.set({
      text: isValid ? 'gps_uploader.controls.valid_duration' : 'gps_uploader.controls.too_short',
      status: isValid ? 'complete' : 'error',
      validate: false,
    });
  }

  private async controlSpeedCoordinates(coordinates: Coordinate[]): Promise<void> {
    this.controlGpsSpeed.update((c) => ({ ...c, status: 'in_progress' }));
    await this.delay(600);
    const hasSpeed = coordinates.some((c) => c.speed > 0);
    this.controlGpsSpeed.set({
      text: hasSpeed
        ? 'gps_uploader.controls.speed_present'
        : 'gps_uploader.controls.speed_missing',
      status: 'complete',
      validate: !hasSpeed,
    });
  }

  private async controlData(coordinates: Coordinate[]): Promise<void> {
    this.controlGpsData.update((c) => ({ ...c, status: 'in_progress' }));
    await this.delay(600);
    const hasElevation = coordinates.some((c) => !isNaN(c.elevation));
    this.controlGpsData.set({
      text: hasElevation
        ? 'gps_uploader.controls.data_complete'
        : 'gps_uploader.controls.altitude_missing',
      status: hasElevation ? 'complete' : 'error',
      validate: false,
    });
  }

  clearVideoInput(): void {
    this.inputVideoRefs().forEach((input: ElementRef<HTMLInputElement>) => {
      input.nativeElement.value = '';
    });
  }

  /**
   * Calcule et stocke le décalage entre la vidéo et le tracé GPS.
   * offset = coo_time du point GPS sélectionné − temps courant de la vidéo
   */
  synchronize(): void {
    const videoTime = this.syncroTime();
    const gpsCoord = this.syncMarkerCoordinate();
    if (videoTime === undefined || !gpsCoord) return;

    const coords = this.syncedCoordinates();
    const gpsStartTime = coords[0]?.coo_time ?? 0;
    const gpsDuration = this.gpsFileData()?.duration ?? 0;
    const videoDuration = this.videoFileData()?.duration ?? 0;

    const offset = gpsCoord.coo_time - videoTime - gpsStartTime;

    if (offset < 0) {
      console.error(
        '[Syncro] Impossible : le point GPS est trop proche du début du tracé (offset négatif :',
        offset,
        's)',
      );
      return;
    }

    if (offset + videoDuration > gpsDuration + SYNC_DURATION_TOLERANCE_SECONDS) {
      console.error(
        '[Syncro] Impossible : la vidéo dépasse la fin du tracé GPS (offset + vidéo =',
        offset + videoDuration,
        's, GPS =',
        gpsDuration,
        's)',
      );
      return;
    }

    this.progressInVideo.set(gpsCoord.coo_time - videoTime);
    this.videoStartTime.set(Math.floor(gpsCoord.coo_time) - Math.floor(videoTime));
    // Synchro validée : le pin de repère n'a plus de raison d'être.
    this.syncMarkerCoordinate.set(undefined);
    this.syncMode.set(false);
  }

  /**
   * Valide la synchronisation en alignant le début de la vidéo sur le premier point GPS.
   * Utilisé quand les durées sont similaires et que l'utilisateur confirme que c'est déjà synchronisé.
   */
  validateAlignedSync(): void {
    const firstCoord: Coordinate | undefined = this.syncedCoordinates()[0];
    if (!firstCoord) return;
    this.progressInVideo.set(firstCoord.coo_time);
    this.videoStartTime.set(Math.floor(firstCoord.coo_time));
    this.syncMarkerCoordinate.set(undefined);
    this.syncMode.set(false);
  }

  /** Annule la synchro validée pour pouvoir replacer un point sur la carte. */
  resetSync(): void {
    this.progressInVideo.set(undefined);
    this.videoStartTime.set(undefined);
    this.mediaForm?.patchValue({ syncroTime: null });
    // Le champ avait été marqué « touché » à la validation de la synchro : sans ça, l'erreur
    // « champ requis » s'afficherait dès que vidéo et GPS sont de nouveau présents.
    this.mediaForm?.get('syncroTime')?.markAsUntouched();
  }

  /** Efface toute la synchro (suppression de la vidéo ou du GPS). */
  clearSync(): void {
    this.resetSync();
    this.syncMarkerCoordinate.set(undefined);
    this.hoveredCoordinate.set(undefined);
    this.syncMode.set(false);
  }

  reset(): void {
    const videoUrl: string | undefined = this.videoUrl();
    if (videoUrl) URL.revokeObjectURL(videoUrl);
    this.videoFile.set(undefined);
    this.gpsFile.set(undefined);
    this.videoUrl.set(undefined);
    this.videoFileData.set(undefined);
    this.gpsFileData.set(undefined);
    this.chartData.set(undefined);
    this.chartLabels.set(undefined);
    this.chartDistances.set(undefined);
    this.syncedCoordinates.set([]);
    this.syncMarkerCoordinate.set(undefined);
    this.progressInVideo.set(undefined);
    this.videoStartTime.set(undefined);
    this.hoveredCoordinate.set(undefined);
    this.gpsTrackValidated.set(false);
    this.isLoadingTrack.set(false);
    this.resetControls();
    this.clearVideoInput();
  }
}
